import { useEffect, useCallback, useRef, useState } from 'react';

/*
├── ProctoringRestrictions.tsx
│   ├── Type Definitions
│   ├── Individual Hooks (7)
│   ├── Composite Hook (1)
│   └── Utility Functions
*/

// ==================== TYPE DEFINITIONS ====================

export interface ProctoringConfig {
  fullScreen?: boolean;
  detectTabSwitch?: boolean;
  blockCopyPaste?: boolean;
  blockRightClick?: boolean;
  blockKeyboardShortcuts?: boolean;
  trackIdle?: boolean;
  trackGeolocation?: boolean;
  detectMultipleSessions?: boolean;
}

interface IdleTimeData {
  idleStartTime: number;
  idleEndTime: number;
}

export interface ProctoringCallbacks {
  onFullScreenExit?: () => void;
  onTabSwitch?: (isHidden: boolean) => void;
  onCopyAttempt?: (copyData: { timestamp: number; text: string }) => void;
  onPasteAttempt?: (pasteData: { timestamp: number; text: string }) => void;
  onRightClickAttempt?: () => void;
  onBlockedShortcut?: (key: string) => void;
  onIdle?: (idleTime: number) => void;
  onActive?: () => void;
  onIdleTimeData?: IdleTimeData[];
  onGeolocationChange?: (coords: GeolocationCoordinates) => void;
  onGeolocationError?: (error: GeolocationPositionError) => void;
  onMultipleSessionDetected?: () => void;
}

export interface IdleConfig {
  idleThreshold?: number; // milliseconds before considering user idle (default: 60000 = 1 min)
  checkInterval?: number; // how often to check (default: 5000 = 5 sec)
}

export interface GeolocationConfig {
  trackInterval?: number; // how often to track location (default: 300000 = 5 min)
  enableHighAccuracy?: boolean;
}

// ==================== 1. FULL-SCREEN ENFORCEMENT ====================

// Helper function to check if currently in fullscreen (supports all browser variants)
const isCurrentlyInFullScreen = (): boolean => {
  return !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).msFullscreenElement
  );
};

export const useFullScreenEnforcement = (
  enabled: boolean = true,
  onFullScreenExit?: () => void
) => {
  const [isFullScreen, setIsFullScreen] = useState(() => {
    // Initialize state based on current fullscreen status
    return enabled ? isCurrentlyInFullScreen() : false;
  });
  const fullScreenExitRef = useRef<number[]>([]);
  const hasCheckedInitialMountRef = useRef(false);
  const onFullScreenExitRef = useRef(onFullScreenExit);

  // Keep the callback ref up to date
  useEffect(() => {
    onFullScreenExitRef.current = onFullScreenExit;
  }, [onFullScreenExit]);

  const requestFullScreen = useCallback(() => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen().catch((err) => {
        console.error('Failed to enter fullscreen:', err);
      });
    }
  }, []);

  const exitFullScreen = useCallback(() => {
    if (document.exitFullscreen && document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error('Failed to exit fullscreen:', err);
      });
    }
  }, []);

  // Check on mount only once
  useEffect(() => {
    if (!enabled || hasCheckedInitialMountRef.current) return;

    const initialFullScreenStatus = isCurrentlyInFullScreen();
    setIsFullScreen(initialFullScreenStatus);
    hasCheckedInitialMountRef.current = true;

    if (!initialFullScreenStatus) {
      fullScreenExitRef.current.push(Date.now());
      onFullScreenExitRef.current?.();
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    const handleFullScreenChange = () => {
      const isCurrentlyFullScreen = isCurrentlyInFullScreen();
      setIsFullScreen(isCurrentlyFullScreen);

      if (!isCurrentlyFullScreen) {
        fullScreenExitRef.current.push(Date.now());
        onFullScreenExitRef.current?.();
      }

      // if (!isCurrentlyFullScreen) {
      //   onFullScreenExit?.();
      //   // Optionally re-request fullscreen
      //   setTimeout(() => {
      //     requestFullScreen();
      //   }, 100);
      // }
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);
    document.addEventListener('MSFullscreenChange', handleFullScreenChange);

    // Request fullscreen on mount
    // requestFullScreen();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullScreenChange
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullScreenChange
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullScreenChange
      );
    };
  }, [enabled]);

  return {
    isFullScreen,
    requestFullScreen,
    exitFullScreen,
    fullScreenExitCount: fullScreenExitRef.current,
  };
};

// ==================== 2. TAB/WINDOW SWITCH DETECTION ====================

export const useTabSwitchDetection = (
  enabled: boolean = true,
  onTabSwitch?: (isHidden: boolean) => void
) => {
  const [isTabActive, setIsTabActive] = useState(true);
  const switchCountRef = useRef<number[]>([]);

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      const isHidden = document.hidden;
      setIsTabActive(!isHidden);

      if (isHidden) {
        switchCountRef.current.push(Date.now());
      }

      onTabSwitch?.(isHidden);
    };

    const handleBlur = () => {
      setIsTabActive(false);
      onTabSwitch?.(false);
    };

    const handleFocus = () => {
      setIsTabActive(true);
      onTabSwitch?.(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, onTabSwitch]);

  return { isTabActive, switchCount: switchCountRef.current };
};

// ==================== 3. COPY/PASTE & RIGHT-CLICK RESTRICTION ====================

export const useCopyPasteRestriction = (
  enabled: boolean = true,
  callbacks?: {
    onCopyAttempt?: (copyData: { timestamp: number; text: string }) => void;
    onPasteAttempt?: (pasteData: { timestamp: number; text: string }) => void;
  }
) => {
  const copyAttemptsRef = useRef<{ timestamp: number; text: string }[]>([]);
  const pasteAttemptsRef = useRef<{ timestamp: number; text: string }[]>([]);

  useEffect(() => {
    if (!enabled) return;

    const handleCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      const copyData = {
        timestamp: Date.now(),
        text: window.getSelection()?.toString() || '',
      };
      copyAttemptsRef.current.push(copyData);
      callbacks?.onCopyAttempt?.(copyData);
      return false;
    };

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      const pasteData = {
        timestamp: Date.now(),
        text: e.clipboardData?.getData('text') || '',
      };
      pasteAttemptsRef.current.push(pasteData);
      callbacks?.onPasteAttempt?.(pasteData);
      return false;
    };

    const handleCut = (e: ClipboardEvent) => {
      e.preventDefault();
      const copyData = {
        timestamp: Date.now(),
        text: window.getSelection()?.toString() || '',
      };
      copyAttemptsRef.current.push(copyData);
      callbacks?.onCopyAttempt?.(copyData);
      return false;
    };

    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('cut', handleCut);

    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('cut', handleCut);
    };
  }, [enabled, callbacks]);

  return {
    copyAttempts: copyAttemptsRef.current,
    pasteAttempts: pasteAttemptsRef.current,
  };
};

export const useRightClickRestriction = (
  enabled: boolean = true,
  onRightClickAttempt?: () => void
) => {
  const rightClickAttemptsRef = useRef<{ timestamp: number }[]>([]);

  useEffect(() => {
    if (!enabled) return;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      rightClickAttemptsRef.current.push({ timestamp: Date.now() });
      onRightClickAttempt?.();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [enabled, onRightClickAttempt]);

  return { rightClickAttempts: rightClickAttemptsRef.current };
};

// ==================== 4. KEYBOARD SHORTCUT BLOCKING ====================

export const useKeyboardShortcutBlocking = (
  enabled: boolean = true,
  onBlockedShortcut?: (key: string) => void
) => {
  const blockedAttemptsRef = useRef<{ timestamp: number; key: string }[]>([]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const { ctrlKey, metaKey, shiftKey, altKey, key } = e;

      // Block common shortcuts
      const blockedCombinations = [
        // Developer tools
        (ctrlKey || metaKey) &&
          shiftKey &&
          (key === 'I' ||
            key === 'i' ||
            key === 'J' ||
            key === 'j' ||
            key === 'C' ||
            key === 'c'),
        (ctrlKey || metaKey) && (key === 'U' || key === 'u'), // View source
        key === 'F12', // Dev tools

        // // Copy/Paste
        // (ctrlKey || metaKey) && (key === 'C' || key === 'c'),
        // (ctrlKey || metaKey) && (key === 'V' || key === 'v'),
        // (ctrlKey || metaKey) && (key === 'X' || key === 'x'),

        // Print
        (ctrlKey || metaKey) && (key === 'P' || key === 'p'),

        // Print Screen
        key === 'PrintScreen',

        // Save
        (ctrlKey || metaKey) && (key === 'S' || key === 's'),

        // Find
        (ctrlKey || metaKey) && (key === 'F' || key === 'f'),

        // Select all (optional - might want to allow this)
        // (ctrlKey || metaKey) && (key === 'A' || key === 'a'),

        // Refresh
        key === 'F5',
        (ctrlKey || metaKey) && (key === 'R' || key === 'r'),

        // Alt+Tab (difficult to block completely due to OS level)
        altKey && key === 'Tab',
      ];

      if (blockedCombinations.some((blocked) => blocked)) {
        e.preventDefault();
        e.stopPropagation();
        blockedAttemptsRef.current.push({ timestamp: Date.now(), key: key });
        onBlockedShortcut?.(
          `${ctrlKey ? 'Ctrl+' : ''}${shiftKey ? 'Shift+' : ''}${
            altKey ? 'Alt+' : ''
          }${key}`
        );
        return false;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [enabled, onBlockedShortcut]);

  return { blockedAttempts: blockedAttemptsRef.current };
};

// ==================== 5. IDLE & INACTIVITY TRACKING ====================

export const useIdleTracking = (
  enabled: boolean = true,
  config: IdleConfig = {},
  callbacks?: {
    onIdle?: (idleTime: number) => void;
    onActive?: () => void;
    idletimeData?: (
      idletimeData: {
        idleStartTime: number;
        idleEndTime: number;
      }[]
    ) => void;
  }
) => {
  const { idleThreshold = 60000, checkInterval = 5000 } = config;
  const [isIdle, setIsIdle] = useState(false);
  const [idleTime, setIdleTime] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const checkIntervalRef = useRef<NodeJS.Timeout>();
  const [idletimeData, setIdletimeData] = useState<IdleTimeData[]>([]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (isIdle) {
      setIsIdle(false);
      callbacks?.onActive?.();
      setIdletimeData((prevState) => {
        const lastItem = prevState[prevState.length - 1];
        return [
          ...prevState.slice(0, -1),
          { ...lastItem, idleEndTime: Date.now() },
        ];
      });
    }
    setIdleTime(0);
  }, [isIdle, callbacks]);

  useEffect(() => {
    if (!enabled) return;

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    activityEvents.forEach((event) => {
      document.addEventListener(event, resetActivity);
    });

    checkIntervalRef.current = setInterval(() => {
      // console.log('idle check every(ms):', checkInterval);
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      setIdleTime(timeSinceActivity);

      if (timeSinceActivity >= idleThreshold && !isIdle) {
        setIsIdle(true);
        callbacks?.onIdle?.(timeSinceActivity);
        setIdletimeData((prevState) => [
          ...prevState,
          { idleStartTime: Date.now(), idleEndTime: 0 },
        ]);
      }
    }, checkInterval);

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, resetActivity);
      });
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [
    enabled,
    idleThreshold,
    checkInterval,
    isIdle,
    resetActivity,
    callbacks,
    setIdletimeData,
  ]);

  return { isIdle, idleTime, idletimeData };
};

// ==================== 6. GEOLOCATION TRACKING ====================

// export const useGeolocationTracking = (
//   enabled: boolean = true,
//   config: GeolocationConfig = {},
//   callbacks?: {
//     onGeolocationChange?: (coords: GeolocationCoordinates) => void;
//     onGeolocationError?: (error: GeolocationPositionError) => void;
//   }
// ) => {
//   const { trackInterval = 300000, enableHighAccuracy = true } = config;
//   const [location, setLocation] = useState<GeolocationCoordinates | null>(null);
//   const [locationError, setLocationError] = useState<string | null>(null);
//   const watchIdRef = useRef<number>();

//   useEffect(() => {
//     if (!enabled || !navigator.geolocation) {
//       if (!navigator.geolocation) {
//         setLocationError('Geolocation not supported');
//       }
//       return;
//     }

//     const handleSuccess = (position: GeolocationPosition) => {
//       setLocation(position.coords);
//       setLocationError(null);
//       callbacks?.onGeolocationChange?.(position.coords);
//     };

//     const handleError = (error: GeolocationPositionError) => {
//       setLocationError(error.message);
//       callbacks?.onGeolocationError?.(error);
//     };

//     // Get initial position
//     navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
//       enableHighAccuracy,
//       timeout: 10000,
//       maximumAge: 0,
//     });

//     // Watch position changes
//     watchIdRef.current = navigator.geolocation.watchPosition(
//       handleSuccess,
//       handleError,
//       {
//         enableHighAccuracy,
//         timeout: 10000,
//         maximumAge: trackInterval,
//       }
//     );

//     return () => {
//       if (watchIdRef.current !== undefined) {
//         navigator.geolocation.clearWatch(watchIdRef.current);
//       }
//     };
//   }, [enabled, trackInterval, enableHighAccuracy, callbacks]);

//   return { location, locationError };
// };

// export interface GeolocationConfig {
//   trackInterval?: number;
//   enableHighAccuracy?: boolean;
//   timeout?: number;
//   maximumAge?: number;
// }
// export interface GeolocationCallbacks {
//   onGeolocationChange?: (coords: GeolocationCoordinates) => void;
//   onGeolocationError?: (error: GeolocationPositionError) => void;
//   onPermissionChange?: (state: PermissionState) => void;
// }
// export const useGeolocationTracking = (
//   enabled: boolean = true,
//   config: GeolocationConfig = {},
//   callbacks?: GeolocationCallbacks
// ) => {
//   const [location, setLocation] = useState<GeolocationCoordinates | null>(null);
//   const [locationError, setLocationError] = useState<string | null>(null);
//   const watchIdRef = useRef<number>();
//   const lastCoordsRef = useRef<GeolocationCoordinates | null>(null);

//   useEffect(() => {
//     if (!enabled || !navigator.geolocation) {
//       if (!navigator.geolocation) {
//         setLocationError('Geolocation not supported');
//       }
//       return;
//     }

//     const handleSuccess = (position: GeolocationPosition) => {
//       const newCoords = position.coords;

//       // Simple coordinate comparison
//       const hasChanged =
//         !lastCoordsRef.current ||
//         lastCoordsRef.current.latitude !== newCoords.latitude ||
//         lastCoordsRef.current.longitude !== newCoords.longitude;

//       // console.log('useGeolocationTracking:', {
//       //   previousLatitude: lastCoordsRef.current?.latitude,
//       //   previousLongitude: lastCoordsRef.current?.longitude,
//       //   newLatitude: newCoords.latitude,
//       //   newLongitude: newCoords.longitude,
//       //   hasChanged: hasChanged,
//       // });

//       if (hasChanged) {
//         setLocation(newCoords);
//         setLocationError(null);
//         lastCoordsRef.current = newCoords;
//         callbacks?.onGeolocationChange?.(newCoords);
//       }
//     };

//     const handleError = (error: GeolocationPositionError) => {
//       setLocationError(error.message);
//       callbacks?.onGeolocationError?.(error);
//     };

//     // Get initial position
//     navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
//       enableHighAccuracy: config.enableHighAccuracy ?? true,
//       timeout: config.timeout ?? 10000,
//       maximumAge: config.maximumAge ?? 0,
//     });

//     // Watch position changes
//     watchIdRef.current = navigator.geolocation.watchPosition(
//       handleSuccess,
//       handleError,
//       {
//         enableHighAccuracy: config.enableHighAccuracy ?? true,
//         timeout: config.timeout ?? 10000,
//         maximumAge: config.trackInterval ?? 300000,
//       }
//     );

//     return () => {
//       if (watchIdRef.current !== undefined) {
//         navigator.geolocation.clearWatch(watchIdRef.current);
//       }
//       lastCoordsRef.current = null;
//     };
//   }, [enabled, config, callbacks]);

//   return { location, locationError };
// };

// ==================== 7. MULTIPLE SESSION DETECTION ====================

interface SessionData {
  sessionId: string;
  tabId: string;
  timestamp: number;
}

export const useMultipleSessionDetection = (
  enabled: boolean = true,
  sessionKey: string = 'proctoring_session',
  onMultipleSessionDetected?: () => void
) => {
  const [hasMultipleSessions, setHasMultipleSessions] = useState(false);
  const [multipleSessionsData, setMultipleSessionsData] = useState<number[]>(
    []
  );
  const sessionIdRef = useRef<string>(`session_${Date.now()}_${Math.random()}`);
  const tabIdRef = useRef<string>(`tab_${Date.now()}_${Math.random()}`);
  const hasDetectedRef = useRef<boolean>(false);
  const onMultipleSessionDetectedRef = useRef(onMultipleSessionDetected);
  const isInitialMountRef = useRef(true);

  // Keep the callback ref up to date
  useEffect(() => {
    onMultipleSessionDetectedRef.current = onMultipleSessionDetected;
  }, [onMultipleSessionDetected]);

  useEffect(() => {
    if (!enabled) return;

    const getSessionData = (): SessionData => ({
      sessionId: sessionIdRef.current,
      tabId: tabIdRef.current,
      timestamp: Date.now(),
    });

    const parseSessionData = (value: string | null): SessionData | null => {
      if (!value) return null;
      try {
        return JSON.parse(value) as SessionData;
      } catch {
        // Handle legacy format (plain string sessionId)
        return {
          sessionId: value,
          tabId: 'unknown',
          timestamp: Date.now(),
        };
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== sessionKey) return;

      const newSessionData = parseSessionData(e.newValue);
      if (!newSessionData) return;

      // If the new session has a different tab ID, it means another tab opened
      // This is the OLD tab detecting the new tab, so we update state but don't trigger the callback
      // (we only want to show popup on the new tab)
      if (newSessionData.tabId !== tabIdRef.current) {
        setHasMultipleSessions(true);
        // Update multipleSessionsData to track the detection, but don't call the callback
        setMultipleSessionsData((prevState) => {
          // Only add if this timestamp hasn't been added yet (avoid duplicates)
          const lastTimestamp = prevState[prevState.length - 1];
          const currentTime = Date.now();
          // Add if array is empty or if the last timestamp is more than 1 second old
          // This prevents duplicate entries from rapid storage events
          if (!lastTimestamp || currentTime - lastTimestamp > 1000) {
            return [...prevState, currentTime];
          }
          return prevState;
        });
        // Don't call onMultipleSessionDetected here - only on the new tab
      }
    };

    // Check if another session exists (only on initial mount)
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      const existingSessionValue = localStorage.getItem(sessionKey);
      const existingSessionData = parseSessionData(existingSessionValue);

      if (
        existingSessionData &&
        existingSessionData.tabId !== tabIdRef.current &&
        !hasDetectedRef.current
      ) {
        // This is a NEW tab detecting an existing session with a different tab ID
        // Show popup only on this new tab, and only once
        hasDetectedRef.current = true;
        setHasMultipleSessions(true);
        onMultipleSessionDetectedRef.current?.();
        setMultipleSessionsData((prevState) => [...prevState, Date.now()]);
      }
    }

    // Set this session with both sessionId and tabId
    const currentSessionData = getSessionData();
    localStorage.setItem(sessionKey, JSON.stringify(currentSessionData));

    // Listen for changes (other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Heartbeat to maintain session
    const heartbeat = setInterval(() => {
      const sessionData = getSessionData();
      localStorage.setItem(sessionKey, JSON.stringify(sessionData));
    }, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(heartbeat);
      // Only remove if it's our session
      const currentValue = localStorage.getItem(sessionKey);
      const currentData = parseSessionData(currentValue);
      if (currentData && currentData.tabId === tabIdRef.current) {
        localStorage.removeItem(sessionKey);
      }
    };
  }, [enabled, sessionKey]);

  return {
    hasMultipleSessions,
    sessionId: sessionIdRef.current,
    tabId: tabIdRef.current,
    multipleSessionsData,
  };
};

// ==================== COMPOSITE HOOK ====================

export const useProctoring = (
  config: ProctoringConfig = {},
  callbacks: ProctoringCallbacks = {},
  options?: {
    idleConfig?: IdleConfig;
    geolocationConfig?: GeolocationConfig;
    sessionKey?: string;
  }
) => {
  const fullScreen = useFullScreenEnforcement(
    config.fullScreen,
    callbacks.onFullScreenExit
  );

  const tabSwitch = useTabSwitchDetection(
    config.detectTabSwitch,
    callbacks.onTabSwitch
  );

  const copyPaste = useCopyPasteRestriction(config.blockCopyPaste, {
    onCopyAttempt: callbacks.onCopyAttempt,
    onPasteAttempt: callbacks.onPasteAttempt,
  });

  const rightClick = useRightClickRestriction(
    config.blockRightClick,
    callbacks.onRightClickAttempt
  );

  const keyboard = useKeyboardShortcutBlocking(
    config.blockKeyboardShortcuts,
    callbacks.onBlockedShortcut
  );

  const idle = useIdleTracking(config.trackIdle, options?.idleConfig, {
    onIdle: callbacks.onIdle,
    onActive: callbacks.onActive,
  });

  // const geolocation = useGeolocationTracking(
  //   config.trackGeolocation,
  //   options?.geolocationConfig,
  //   {
  //     onGeolocationChange: callbacks.onGeolocationChange,
  //     onGeolocationError: callbacks.onGeolocationError,
  //   }
  // );

  const multipleSession = useMultipleSessionDetection(
    config.detectMultipleSessions,
    options?.sessionKey,
    callbacks.onMultipleSessionDetected
  );

  return {
    fullScreen,
    tabSwitch,
    copyPaste,
    rightClick,
    keyboard,
    idle,
    // geolocation,
    multipleSession,
  };
};

// ==================== HELPER UTILITIES ====================

export const getProctoringViolationsSummary = (
  proctoringData: ReturnType<typeof useProctoring>
) => {
  return {
    // 1. FULL-SCREEN ENFORCEMENT =========================================
    fullScreenExits: proctoringData.fullScreen.fullScreenExitCount,

    // 2. TAB/WINDOW SWITCH DETECTION ======================================
    tabSwitches: proctoringData.tabSwitch.switchCount,

    // 3. COPY/PASTE & RIGHT-CLICK RESTRICTION ==============================
    copyAttempts: proctoringData.copyPaste.copyAttempts,
    pasteAttempts: proctoringData.copyPaste.pasteAttempts,
    rightClickAttempts: proctoringData.rightClick.rightClickAttempts,

    // 4. KEYBOARD SHORTCUT BLOCKING ======================================
    blockedShortcuts: proctoringData.keyboard.blockedAttempts,

    // 5. IDLE & INACTIVITY TRACKING ======================================
    idletimeData: proctoringData.idle.idletimeData,

    // // 6. GEOLOCATION TRACKING ==============================================
    // currentLocation: proctoringData.geolocation.location,

    // 7. MULTIPLE SESSION DETECTION ======================================
    multipleSessionsData: proctoringData.multipleSession.multipleSessionsData,
  };
};

// ======================== Proctoring Documentation ========================

/*

## 📐 System Architecture Diagram

┌──────────────────────────────────────────────────────────────────┐
│                     Your Application Layer                       │
│  ┌───────────────┐  ┌──────────────┐  ┌─────────────────┐        │
│  │  Exam Page    │  │  Quiz Page   │  │  Homework Page  │        │
│  └───────┬───────┘  └──────┬───────┘  └────────┬────────┘        │
└──────────┼─────────────────┼───────────────────┼─────────────────┘
           │                 │                   │
           │                 │                   │
┌──────────▼─────────────────▼───────────────────▼─────────────────┐
│                   Proctoring Hooks Layer                         │
│  ┌─────────────────────────────────────────────────────────┐     │
│  │              useProctoring (Composite Hook)             │     │
│  │                                                         │     │
│  │  ┌───────────────────────────────────────────────────┐  │     │
│  │  │          Individual Hooks (Modular)               │  │     │
│  │  │                                                   │  │     │
│  │  │  ┌──────────────────────┐  ┌──────────────────┐   │  │     │
│  │  │  │ useFullScreen        │  │ useTabSwitch     │   │  │     │
│  │  │  │ Enforcement          │  │ Detection        │   │  │     │
│  │  │  └──────────────────────┘  └──────────────────┘   │  │     │
│  │  │                                                   │  │     │
│  │  │  ┌──────────────────────┐  ┌──────────────────┐   │  │     │
│  │  │  │ useCopyPaste         │  │ useRightClick    │   │  │     │
│  │  │  │ Restriction          │  │ Restriction      │   │  │     │
│  │  │  └──────────────────────┘  └──────────────────┘   │  │     │
│  │  │                                                   │  │     │
│  │  │  ┌──────────────────────┐  ┌──────────────────┐   │  │     │
│  │  │  │ useKeyboard          │  │ useIdle          │   │  │     │
│  │  │  │ ShortcutBlocking     │  │ Tracking         │   │  │     │
│  │  │  └──────────────────────┘  └──────────────────┘   │  │     │
│  │  │                                                   │  │     │
│  │  │  ┌──────────────────────┐  ┌──────────────────┐   │  │     │
│  │  │  │ useGeolocation       │  │ useMultiple      │   │  │     │
│  │  │  │ Tracking             │  │ SessionDetection │   │  │     │
│  │  │  └──────────────────────┘  └──────────────────┘   │  │     │
│  │  │                                                   │  │     │
│  │  └───────────────────────────────────────────────────┘  │     │
│  └─────────────────────────────────────────────────────────┘     │
└──────────────────────────────┬───────────────────────────────────┘
                               │
                               │ Event Listeners & Browser APIs
                               │
┌──────────────────────────────▼───────────────────────────────────┐
│                      Browser API Layer                           │
│  ┌──────────────┐  ┌──────────────┐  ┌──────────────┐            │
│  │ Fullscreen   │  │ Clipboard    │  │ Keyboard     │            │
│  │ API          │  │ API          │  │ Events       │            │
│  └──────────────┘  └──────────────┘  └──────────────┘            │
│                                                                  │
│  ┌──────────────┐  ┌──────────────┐  ┌──────────────┐            │
│  │ Page         │  │ Geolocation  │  │ LocalStorage │            │
│  │ Visibility   │  │ API          │  │ API          │            │
│  └──────────────┘  └──────────────┘  └──────────────┘            │
└──────────────────────────────────────────────────────────────────┘




## 🔄 Data Flow

User Action (e.g., Copy)
    │
    ▼
Browser Event (ClipboardEvent)
    │
    ▼
Event Listener (in Hook)
    │
    ├─── Prevent Default (Block Action)
    │
    ├─── Update State (copyAttempts++)
    │
    └─── Execute Callback (onCopyAttempt)
         │
         ▼
    Your Application Handler
         │
         ├─── Show UI Warning
         │
         └─── Log to Backend API




## 🎨 Usage Pattern Flow

### Pattern 1: Direct Hook Usage (Individual Hooks)

Component
    │
    ├─── Import: useFullScreenEnforcement
    │
    ├─── Call Hook: useFullScreenEnforcement(true, callback)
    │
    ├─── Get Returns: { isFullScreen, requestFullScreen, exitFullScreen }
    │
    └─── Use in JSX: {!isFullScreen && <Button />}


### Pattern 2: Composite Hook Usage

Component
    │
    ├─── Import: useProctoring
    │
    ├─── Define Config: { fullScreen: true, ... }
    │
    ├─── Call Hook: useProctoring(config, callbacks, options)
    │
    ├─── Get Returns: { fullScreen, tabSwitch, copyPaste, ... }
    │
    └─── Use in JSX: {proctoring.fullScreen.isFullScreen}

*/
